import './codemirror.less';
import { languages } from '../../constants/languages';
import { accountServiceModule } from '../../services/account-service/account-service';
import { TABSET_EVT_SELECT } from '../tabs/tabs';
import {
  FULLSCREEN_EVT_CHANGE,
  fullscreenToggleComponentModule,
} from '../fullscreen-toggle/fullscreen-toggle';
import { codemirrorOptionsComponentModule } from './codemirror-options';
import { UI_LAYOUT_EVT_RESIZE } from '../ui-layout/ui-layout';

export function trackLineWidgets(cm, matcher, createWidget) {
  let lineWidgets = [];
  let timer;
  if (matcher instanceof RegExp) {
    matcher = matcher.test.bind(matcher);
  }

  function update() {
    const _lineWidgets = lineWidgets;
    lineWidgets = [];
    let lineWidgetLines = _lineWidgets.map((lw) =>
      cm.getLineNumber(lw.widget.line),
    );
    cm.eachLine((lineHandle) => {
      let lineNumber = cm.getLineNumber(lineHandle);
      if (matcher(lineHandle.text)) {
        let widgetIdx = lineWidgetLines.indexOf(lineNumber);
        let previous = _lineWidgets[widgetIdx];
        let { keep, element, options, id } =
          createWidget(lineHandle.text, previous, lineNumber) || {};
        if (keep) {
          lineWidgets.push(previous);
          _lineWidgets[widgetIdx] = null;
        } else {
          if (element) {
            const widget = cm.addLineWidget(lineNumber, element, options);
            lineWidgets.push({ widget, element, id });
          }
        }
      }
    });
    // remove any remaining line widgets, as they didn't get reused
    _lineWidgets.forEach((w) => w && w.widget.clear());
  }

  update();

  cm.on('change', function () {
    if (timer) {
      // eslint-disable-next-line angular/timeout-service
      clearTimeout(timer);
    }
    // eslint-disable-next-line angular/timeout-service
    timer = setTimeout(update, 500);
  });

  return update;
}

export const codemirrorComponentModule = angular
  .module('Q.components.codemirror', [
    accountServiceModule,
    fullscreenToggleComponentModule,
    codemirrorOptionsComponentModule,
  ])
  .constant('uiCodemirrorConfig', {})

  /**
   * @category directive
   *
   * @ngdoc directive
   *
   * @name uiCodemirror
   * @restrict E
   *
   * @description
   * Renders a CodeMirror editor on a node.
   *
   * @param uiCodemirrorOpts {expression} Provide options to the CodeMirror instance
   * @param uiRefresh {expression=} Refresh the CodeMirror instance if this property changes
   * @param autofocus {boolean=} If truthy and this editor is visible, focus in this editor after creation
   * @param placeholder {template=} Enables adding placeholder text to the CodeMirror, which can be multiline if you use &#10; for newlines
   * @param documentId {template=} If provided, should be a unique ID to track cursor position across pages
   * @param codemirrorLabel {template=} Render a label for this CodeMirror
   * @param codemirrorTestFramework {expression=} Render the test framework used in this CodeMirror
   * @param codemirrorOnTestFrameworkClick {function=} Action to evaluate when clicking on the test framework label
   *
   */

  .directive(
    'uiCodemirror',
    function uiCodemirrorDirective($window, $timeout, uiCodemirrorConfig, $q) {
      const documentIdData = {};

      return {
        restrict: 'E',
        require: ['uiCodemirror', '?ngModel', '^?formGroup'],
        controller: function UiCodemirrorCtrl($scope, $element) {
          let ctrl = this;
          let readyPromise = $q.defer();

          ctrl.$onFocusChange = angular.noop;
          ctrl.whenReady = () => readyPromise.promise;
          ctrl.fileInfo = null; // Store file info for code selection context

          ctrl.init = (codemirror) => {
            ctrl.codemirror = codemirror;
            readyPromise.resolve(codemirror);
            codemirror.on('focus', function () {
              ctrl.hasFocus = true;
              $element.addClass('focus');
              ctrl.$onFocusChange(true);
              if ($element.attr('ng-focus')) {
                $scope.$eval($element.attr('ng-focus'));
              }
            });
            codemirror.on('blur', function () {
              ctrl.hasFocus = false;
              $element.removeClass('focus');
              ctrl.$onFocusChange(false);
              if ($element.attr('ng-blur')) {
                $scope.$eval($element.attr('ng-blur'));
              }
            });
            codemirror.on('copy', (cm, e) => {
              if ($scope.disabledOrReadonly) {
                // When disabled, we disable codemirror's copy function so we can ensure the
                // browser is able to copy
                e.codemirrorIgnore = true;
              }
            });

            // Feature 8: Code Selection Context for AI Chat
            // Track code selection but don't send automatically
            // Instead, wait for user to trigger via keyboard shortcut or context menu

            ctrl.currentSelection = null;

            codemirror.on('cursorActivity', function (cm) {
              const selection = cm.getSelection();
              const from = cm.getCursor('from');
              const to = cm.getCursor('to');

              // Check if there's an actual selection (not just cursor position)
              const hasSelection =
                selection &&
                selection.length > 0 &&
                (from.line !== to.line || from.ch !== to.ch);

              if (hasSelection) {
                // Store current selection - use ctrl.fileInfo which is set in postLink
                ctrl.currentSelection = {
                  selectedCode: selection,
                  startLine: from.line + 1, // CodeMirror is 0-indexed
                  endLine: to.line + 1,
                  filePath: ctrl.fileInfo ? ctrl.fileInfo.path : 'unknown',
                  language: ctrl.fileInfo ? ctrl.fileInfo.language : 'text',
                  timestamp: Date.now(),
                };
              } else {
                // Selection cleared
                ctrl.currentSelection = null;
              }
            });

            // Keyboard shortcut: Cmd/Win + L to add selection to chat
            codemirror.on('keydown', function (cm, event) {
              const isMac = /Mac/.test(navigator.platform);
              const modifierKey = isMac ? event.metaKey : event.ctrlKey;

              // Check for Cmd/Win + L (keyCode 76 for 'L')
              if (
                modifierKey &&
                (event.key === 'l' || event.key === 'L' || event.keyCode === 76)
              ) {
                // ALWAYS prevent default to stop browser from opening URL bar
                event.preventDefault();
                event.stopPropagation();

                if (ctrl.currentSelection) {
                  // Send selection to chat
                  $scope.$emit('codemirror:add-to-chat', ctrl.currentSelection);
                }
                return false; // Extra safety to prevent default
              }
            });
          };
        },
        link: postLink,
      };

      /**
       * Configure context menu for adding code selection to AI chat
       * Only shows "Add to Chat" option when:
       * - Code is selected
       * - AI chat feature is enabled
       * - Chat agent tab is available
       */
      function configureContextMenu(codemirror, scope, elem, ctrl) {
        let contextMenu = null;

        // Create context menu element
        function createContextMenu() {
          const menu = document.createElement('div');
          menu.className = 'codemirror-context-menu';
          menu.style.cssText = `
            position: absolute;
            background: white;
            border: 1px solid #ccc;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            padding: 4px 0;
            z-index: 10000;
            display: none;
          `;

          const menuItem = document.createElement('div');
          menuItem.className = 'context-menu-item';
          menuItem.innerHTML = `
            <i class="z-action-ai" style="margin-right: 8px;"></i>
            Add to Chat
            <span style="float: right; margin-left: 20px; color: #999; font-size: 11px;">
              ${/Mac/.test(navigator.platform) ? '⌘L' : 'Ctrl+L'}
            </span>
          `;
          menuItem.style.cssText = `
            padding: 8px 16px;
            cursor: pointer;
            user-select: none;
            display: flex;
            align-items: center;
          `;

          menuItem.addEventListener('mouseenter', function () {
            this.style.background = '#f0f0f0';
          });

          menuItem.addEventListener('mouseleave', function () {
            this.style.background = 'white';
          });

          menuItem.addEventListener('click', function () {
            if (ctrl.currentSelection) {
              scope.$emit('codemirror:add-to-chat', ctrl.currentSelection);
            }
            hideContextMenu();
          });

          menu.appendChild(menuItem);
          document.body.appendChild(menu);
          return menu;
        }

        function showContextMenu(event) {
          // Only show if there's a selection
          if (!ctrl.currentSelection) {
            return;
          }

          // Check if AI chat is available by looking for chat agent iframe
          const chatIframe = document.querySelector(
            'iframe[src*="embed/agent-chat"]',
          );
          if (!chatIframe) {
            return; // Chat not available, don't show menu
          }

          event.preventDefault();
          event.stopPropagation(); // Stop event from bubbling up

          if (!contextMenu) {
            contextMenu = createContextMenu();
          }

          contextMenu.style.display = 'block';
          contextMenu.style.left = event.pageX + 'px';
          contextMenu.style.top = event.pageY + 'px';
        }

        function hideContextMenu() {
          if (contextMenu) {
            contextMenu.style.display = 'none';
          }
        }

        // Listen for right-click on CodeMirror
        const wrapper = codemirror.getWrapperElement();
        wrapper.addEventListener('contextmenu', showContextMenu, true); // Use capture phase

        // Hide context menu when clicking elsewhere
        const hideOnClick = function () {
          hideContextMenu();
        };

        const hideOnRightClick = function (e) {
          // If right-click is outside CodeMirror, hide our menu
          if (!wrapper.contains(e.target)) {
            hideContextMenu();
          }
        };

        document.addEventListener('click', hideOnClick);
        document.addEventListener('contextmenu', hideOnRightClick);

        // Cleanup on destroy
        scope.$on('$destroy', function () {
          wrapper.removeEventListener('contextmenu', showContextMenu);
          document.removeEventListener('click', hideOnClick);
          document.removeEventListener('contextmenu', hideOnRightClick);
          if (contextMenu && contextMenu.parentNode) {
            contextMenu.parentNode.removeChild(contextMenu);
          }
        });
      }

      /**
       * Configures the element in the post link phase
       * @param scope
       * @param elem
       * @param attrs
       * @param uiCodemirrorCtrl
       * @param ngModel
       * @param formGroupCtrl
       */
      function postLink(
        scope,
        elem,
        attrs,
        [uiCodemirrorCtrl, ngModel, formGroupCtrl],
      ) {
        if (angular.isUndefined($window.CodeMirror)) {
          throw new Error('Missing CodeMirror');
        }

        if (!$window.CodeMirror.findModeByName) {
          const find = (name) => {
            const l = languages.getLang(name);
            if (l && l.codemirror) {
              return l;
            }
          };
          $window.CodeMirror.findModeByName = find;
          $window.CodeMirror.findModeByExtension = find;
          $window.CodeMirror.findModeByName = find;
        }

        const codemirrorOptions = angular.extend(
          { value: elem.text() },
          uiCodemirrorConfig.codemirror || {},
          scope.$eval(attrs.uiCodemirrorOpts),
        );
        if (attrs.placeholder) {
          codemirrorOptions.placeholder = attrs.placeholder;
        }

        // Feature 8: Make file info available for code selection context
        if (codemirrorOptions.file) {
          scope.file = codemirrorOptions.file;
          uiCodemirrorCtrl.fileInfo = codemirrorOptions.file; // Also store in controller
        }

        scope.disabledOrReadonly = true;
        if (attrs.disabled || attrs.readonly) {
          scope.disabledOrReadonly = true;
          codemirrorOptions.readOnly = true;
        }

        elem.html('');
        const codemirror = new $window.CodeMirror(
          (cmEl) => elem.append(cmEl),
          codemirrorOptions,
        );

        if (attrs.onCreate) {
          scope.$eval(attrs.onCreate, { codemirror });
        }
        uiCodemirrorCtrl.init(codemirror);

        // Feature 8: Context menu for adding code to AI chat
        configureContextMenu(codemirror, scope, elem, uiCodemirrorCtrl);

        configOptionsWatcher(codemirror, attrs.uiCodemirrorOpts, scope);
        configUiRefreshAttribute(codemirror, attrs.uiRefresh, scope);
        configLabel(codemirror, scope, elem, attrs, uiCodemirrorCtrl);

        configNgModelLink(codemirror, ngModel, scope, uiCodemirrorCtrl);
        configAutoRefresh(codemirror, scope, elem);
        configureDocumentIdData(codemirror, scope, attrs);
        configFocus(codemirror, scope, elem, attrs, formGroupCtrl);
        // onLoad callback
        if (angular.isFunction(codemirrorOptions.onLoad)) {
          scope.$applyAsync(() => {
            codemirrorOptions.onLoad(codemirror);
          });
        }
      }

      /** ******************************************************************************************
       * Watches for changes to the options, and updates the CM as necessary
       * @param codemirror
       * @param uiCodemirrorAttr
       * @param scope
       */
      function configOptionsWatcher(codemirror, uiCodemirrorAttr, scope) {
        if (!uiCodemirrorAttr) {
          return;
        }

        const codemirrorDefaultsKeys = Object.keys($window.CodeMirror.defaults);
        scope.$watch(uiCodemirrorAttr, updateOptions, true);

        function updateOptions(newValues, oldValue) {
          if (!angular.isObject(newValues)) {
            return;
          }
          codemirrorDefaultsKeys.forEach(function (key) {
            if (newValues.hasOwnProperty(key)) {
              if (oldValue && newValues[key] === oldValue[key]) {
                return;
              }

              codemirror.setOption(key, newValues[key]);
            }
          });
        }
      }

      /** ******************************************************************************************
       * Links the ngModel controller to the CM instance
       * @param codemirror
       * @param ngModel
       * @param scope
       * @param uiCodemirrorCtrl
       */
      function configNgModelLink(codemirror, ngModel, scope, uiCodemirrorCtrl) {
        if (!ngModel) {
          return;
        }
        // CodeMirror expects a string, so make sure it gets one.
        // This does not change the model.
        ngModel.$formatters.push(function (value) {
          if (angular.isUndefined(value) || value === null) {
            return '';
          } else if (angular.isObject(value) || angular.isArray(value)) {
            throw new Error(
              'ui-codemirror cannot use an object or an array as a model',
            );
          }
          return value;
        });

        let updatingFromRender = false;

        function render() {
          //Code mirror expects a string so make sure it gets one
          //Although the formatter have already done this, it can be possible that another formatter returns undefined (for example the required directive)
          const safeViewValue = ngModel.$viewValue || '';
          if (getValue() === safeViewValue) return;

          updatingFromRender = true;
          const startedCurrentRenderClean = codemirror.isClean();
          const cursor = codemirror.getCursor();
          const selections = codemirror.somethingSelected()
            ? codemirror.listSelections()
            : false;
          const movedCursor = cursor.line > 0 || cursor.ch > 0;
          codemirror.$nonUserInput = true;
          codemirror.setValue(safeViewValue);
          // Don't set cursor or selection in read-only mode, or CM takes focus
          if (
            movedCursor ||
            !scope.disabledOrReadonly ||
            codemirror.hasFocus()
          ) {
            try {
              // ensure the user doesn't lose their place
              if (selections) {
                codemirror.setSelections(selections);
              } else {
                codemirror.setCursor(cursor);
              }
            } catch (e) {
              // ignore cursor errors
            }
          }
          codemirror.$nonUserInput = false;
          updatingFromRender = false;

          if (startedCurrentRenderClean && safeViewValue) {
            codemirror.clearHistory();
          }
        }

        // ensure the initial model view value is set
        render();

        // Override the ngModelController $render method, which is what gets called when the model is updated.
        // This takes care of the synchronizing the codeMirror element with the underlying model, in the case that it is changed by something else.
        ngModel.$render = render;

        // returns the current editor value
        function getValue() {
          return codemirror.getValue();
        }

        // Keep the ngModel in sync with changes from CodeMirror
        codemirror.on('change', function () {
          // ignore changes if this is updating
          if (updatingFromRender) return;
          scope.$evalAsync(function () {
            let newValue = getValue();
            if (newValue !== ngModel.$viewValue) {
              ngModel.$setViewValue(
                newValue,
                uiCodemirrorCtrl.hasFocus ? undefined : 'blur',
              );
            }
          });
        });

        uiCodemirrorCtrl.$onFocusChange = (hasFocus) => {
          ngModel.$setViewValue(getValue(), hasFocus ? undefined : 'blur');
        };

        scope.$on('$destroy', () => {
          // ensure we trigger a blur change if getting destroyed
          ngModel.$setViewValue(getValue(), 'blur');
        });
      }

      /** ******************************************************************************************
       * Refreshes the CM instance if a ui-refresh attribute changes
       *
       * @param codeMirror
       * @param uiRefreshAttr
       * @param scope
       */
      function configUiRefreshAttribute(codeMirror, uiRefreshAttr, scope) {
        if (!uiRefreshAttr) {
          return;
        }

        scope.$watch(uiRefreshAttr, function (newVal, oldVal) {
          // Skip the initial watch firing
          if (newVal !== oldVal) {
            $timeout(function () {
              codeMirror.refresh();
            });
          }
        });
      }

      /** ******************************************************************************************
       * Enables autofocus
       * @param codemirror
       * @param scope
       * @param elem
       * @param attrs
       * @param formGroupCtrl
       */
      function configFocus(codemirror, scope, elem, attrs, formGroupCtrl) {
        codemirror.refresh();
        if (formGroupCtrl) {
          codemirror.on('focus', () => formGroupCtrl.setFocus(true));
          codemirror.on('blur', () => formGroupCtrl.setFocus(false));
        }
        if (scope.$eval(attrs.autofocus)) {
          $timeout(
            () => {
              if (elem.children().isVisible()) {
                codemirror.focus();
              }
            },
            100,
            false,
          );
        }
      }

      /** ******************************************************************************************
       * Sets up an optional label inside the CM itself
       * @param codemirror
       * @param scope
       * @param elem
       * @param attrs
       * @param uiCodemirrorCtrl
       */
      function configLabel(codemirror, scope, elem, attrs, uiCodemirrorCtrl) {
        let tfWrapper;
        let labelWrapper;

        const label = attrs.codemirrorLabel;
        const testFramework = attrs.codemirrorTestFramework;

        function updateLabel() {
          if (labelWrapper) {
            labelWrapper.text(attrs.codemirrorLabel);
          }
          const testFramework = attrs.codemirrorTestFramework;
          if (tfWrapper && testFramework) {
            const tfId = scope.$eval(testFramework);
            tfWrapper.text(languages.frameworks[tfId] || tfId);
          }
        }

        if (label || testFramework) {
          uiCodemirrorCtrl.cmLabel = angular.element(
            '<div class="codemirror-label"></div>',
          );
          elem
            .querySelector('.CodeMirror-lines')
            .prepend(uiCodemirrorCtrl.cmLabel);
          if (label) {
            labelWrapper = angular.element('<span class="label-text"></span>');
            uiCodemirrorCtrl.cmLabel.append(labelWrapper);
            attrs.$observe('codemirrorLabel', updateLabel);
          }
          if (testFramework) {
            if (attrs.codemirrorOnTestFrameworkClick) {
              tfWrapper = angular.element(
                '<a class="codemirror-test-framework-link"></a>',
              );
              tfWrapper.on('click', () => {
                scope.$applyAsync(() =>
                  scope.$eval(attrs.codemirrorOnTestFrameworkClick),
                );
              });
            } else {
              tfWrapper = angular.element('<span></span>');
            }
            tfWrapper.addClass('codemirror-test-framework');
            uiCodemirrorCtrl.cmLabel.append(tfWrapper);
            scope.$watch(attrs.codemirrorTestFramework, updateLabel);
          }
          updateLabel();
        }
      }

      /** ******************************************************************************************
       * Automatically refresh the CM instance based on certain events
       * @param codemirror
       * @param scope
       * @param elem
       */
      function configAutoRefresh(codemirror, scope, elem) {
        if (codemirror) {
          let refreshEvent = null;
          const refresh = () => {
            if (refreshEvent) window.clearTimeout(refreshEvent);
            // we're running this outside Angular on purpose
            // eslint-disable-next-line angular/timeout-service
            refreshEvent = window.setTimeout(() => {
              if (elem.isVisible('basic')) {
                codemirror.refresh();
              }
            }, 10);
          };
          scope.$on(TABSET_EVT_SELECT, refresh);
          scope.$on(UI_LAYOUT_EVT_RESIZE, refresh);
          scope.$on(FULLSCREEN_EVT_CHANGE, refresh);
          scope.$watch(() => {
            // check on every watch if we need to refresh
            // (will automatically be checked after the current digest completes)
            if (codemirror && !elem.isVisible('basic')) {
              refresh();
            }
          });

          scope.$watch(
            () => elem.attr('disabled') || elem.attr('readonly'),
            (disabled) => {
              scope.disabledOrReadonly = !!disabled;
              codemirror.setOption('readOnly', scope.disabledOrReadonly);
            },
          );
        }
      }

      function configureDocumentIdData(codemirror, scope, attrs) {
        if (attrs.documentId) {
          codemirror.$nonUserInput = true;
          // track unique document ID data
          const docId = scope.$eval(attrs.documentId);
          if (!documentIdData[docId]) {
            documentIdData[docId] = {};
          }
          const docData = documentIdData[docId];
          if (docData.cursor) {
            codemirror.setCursor(docData.cursor);
          }
          if (docData.history) {
            codemirror.setHistory(docData.history);
          }
          scope.$on('$destroy', () => {
            // preserve cursors between views
            docData.cursor = codemirror.getCursor();
            docData.history = codemirror.getHistory();
          });
          codemirror.$nonUserInput = false;
        }
      }
    },
  );
